import styles from './SectionsGenerator.module.css';
import React, {useState} from 'react';
import cn from 'classnames';
import useApi from '../../../../hooks/useApi.hook';
import {Textarea} from '../../../textarea/Textarea';
import Spinner from '../../../Spinner/Spinner';
import Button from '../../../Button/Button';
import {CustomSelect} from '../../../Select/Select';
import {InsInputForm} from './InsInputForm/InsInputForm';
import {PrimaryBriefingInputForm} from './PrimaryBriefingInputForm/PrimaryBriefingInputForm';
import Input from '../../../Input/Input';
import useTemplateRegistry from '../../../../hooks/useTemplateRegistry.hook';
import {notifyDownloadQueued} from '../../../../helpers/downloadsEvents';


export function SectionsGenerator({optionsProf, professionDict}) {
	const api = useApi();
	const baseGenerateUrl = '/documents/sections_generate/';
	const baseDownloadUrl = '/documents/download_sections_document/';
	const TEMPLATE_CODES = ['iot_blank', 'primary_briefing_program'];

	const [loading, setLoading] = useState(false);
	const [smallLoading, setSmallLoading] = useState(false);
	const [error, setError] = useState(undefined);
	const [selectedProfOption, setSelectedProfOption] = useState(null);
	const [profession, setProfession] = useState('');
	const [description, setDescription] = useState('');
	const [materials, setMaterials] = useState('');
	const [sections, setSections] = useState({});
	const [selectedSection, setSelectedSection] = useState(null);
	const [textareaValue, setTextareaValue] = useState('');
	const [managerTitle, setManagerTitle] = useState('');
	const [equipmentHint, setEquipmentHint] = useState('');
	const [searchIndex, setSearchIndex] = useState(null);

	const {byCode: templatesByCode} = useTemplateRegistry();
	const templateOptions = TEMPLATE_CODES.map(code => ({
		value: code,
		label: templatesByCode[code]?.title ?? code
	}));

	const handleTemplateChange = (option) => {
		setSelectedTemplate(option);
		setSections({});
		setSelectedSection(null);
		setTextareaValue('');
		setError(undefined);
	};

	const [selectedTemplate, setSelectedTemplate] = useState(templateOptions[0]);


	const handleGenerate = async () => {
		setError(undefined);
		setLoading(true);
		const template = selectedTemplate.value;

		let payload;
		if (selectedTemplate.value === 'iot_blank') {
			payload = {
				search_index: searchIndex,
				profession: profession,
				description: description,
				sizo: materials.split(',').map(s => s.trim()).filter(Boolean)
			};
		}

		if (selectedTemplate.value === 'primary_briefing_program') {
			payload = {
				search_index: searchIndex,
				profession: profession,
				manager_title: managerTitle,
				equipment_hint: equipmentHint
			};
		}

		try {
			const { data } = await api.post(`${baseGenerateUrl}${template}`, payload);
			setSections(data);

			const firstKey = Object.keys(data)[0];
			if (firstKey) {
				setSelectedSection({value: firstKey, label: data[firstKey].title});
				setTextareaValue(data[firstKey].text);
			}
		} catch (e) {
			const msg = e?.response?.data?.detail || e?.message || 'Ошибка при запросе к серверу';
			setError(msg);
		} finally {
			setLoading(false);
		}
	};

	const handleSectionChange = (newSection) => {
		if (selectedSection) {
			setSections(prev => ({
				...prev,
				[selectedSection.value]: {
					...prev[selectedSection.value],
					text: textareaValue
				}
			}));
		}

		setSelectedSection(newSection);
		setTextareaValue(sections[newSection.value]?.text || '');
	};

	const handleTextareaChange = (value) => {
		setTextareaValue(value);
		if (selectedSection?.value) {
			setSections(prev => ({
				...prev,
				[selectedSection.value]: {
					...prev[selectedSection.value],
					text: value
				}
			}));
		}
	};

	const handleDownload = async () => {
		setError(undefined);
		setLoading(true);
		const template = selectedTemplate.value;

		try {
			const payloadSections = {};
			for (const key in sections) {
				payloadSections[key] = {
					title: sections[key].title,
					text: sections[key].text
				};
			}

			const response = await api.post(
				`${baseDownloadUrl}${template}`,
				{
					profession,
					sections: payloadSections
				}
			);
			notifyDownloadQueued(response.data);
		} catch (e) {
			const msg = e?.response?.data?.detail || e?.message || 'Ошибка при запросе к серверу';
			setError(msg);
		} finally {
			setLoading(false);
		}
	};

	const sectionOptions = Object.keys(sections).map(key => ({
		value: key,
		label: sections[key].title
	}));

	const handleProfessionChange = async (option) => {
		setSelectedProfOption(option);
		setMaterials('');
		setDescription('');
		setEquipmentHint('');

		if (!option) {
			setProfession('');
			return;
		}

		setSmallLoading(true);
		const prof = professionDict[option.value];
		setProfession(prof?.title || '');

		try {
			const { data } = await api.get(`/professions/${option.value}`);

			const materialTitles =
			data?.norm?.material_norm_types
				?.map(item => item.material_type?.title)
				.filter(Boolean) || [];

			setMaterials(materialTitles.join(', '));
			setDescription(data?.description || '');
			setEquipmentHint(data?.description || '');
		} catch (e) {
			console.error(e);
			setMaterials('');
		} finally {
			setSmallLoading(false);
		}
	};

	return (
		<>
			<div className={styles.container}>
				{error && (
					<div className={styles.error} onClick={() => setError(undefined)}>
						{error} <span className={styles.errorClose}>✖</span>
					</div>
				)}
				<h1 className={styles.title}>Генератор секционных документов</h1>
				<div className={styles.formWrapper}>
					<div className={styles.inputs}>
						<CustomSelect
							className={styles.my_wider_select}
							value={templateOptions.find(o => o.value === selectedTemplate?.value) || null}
							options={templateOptions}
							placeholder="Тип документа"
							onChange={handleTemplateChange}
							width="100%"
						/>
						<Input
							placeholder="Поисковый индекс"
							value={searchIndex}
							onChange={(e) => setSearchIndex(e.target.value)}
						/>

						{selectedTemplate?.value === 'iot_blank' && (
							<InsInputForm
								optionsProf={optionsProf}
								selectedProfOption={selectedProfOption}
								handleProfessionChange={handleProfessionChange}
								description={description}
								setDescription={setDescription}
								materials={materials}
								smallLoading={smallLoading}
							/>
						)}

						{selectedTemplate?.value === 'primary_briefing_program' && (
							<PrimaryBriefingInputForm
								optionsProf={optionsProf}
								selectedProfOption={selectedProfOption}
								handleProfessionChange={handleProfessionChange}
								managerTitle={managerTitle}
								setManagerTitle={setManagerTitle}
								equipmentHint={equipmentHint}
								setEquipmentHint={setEquipmentHint}
							/>
						)}

					</div>

					<div className={styles.output}>
						<div className={styles.button}>
							<Button onClick={handleGenerate} disabled={loading}>
								{loading ? 'Запрос в обработке...' : 'Сформировать'}
							</Button>
						</div>
						<CustomSelect
							className={styles.my_wider_select}
							options={sectionOptions}
							value={selectedSection}
							placeholder="Выберите секцию"
							onChange={handleSectionChange}
							width="100%"
						/>
						<div className={styles.textareaContainer}>
							<Textarea
								text={textareaValue}
								setText={handleTextareaChange}
								placeholder="Текст инструкции будет здесь"
								disabled={false}
								className={styles.textarea_output}
							/>
							{loading && (
								<div className={styles.spinnerOverlay}>
									<Spinner showSeconds={true} />
								</div>
							)}
						</div>
					</div>
				</div>

				<div className={styles.button}>
					<Button
						onClick={handleDownload}
						disabled={!profession || !Object.keys(sections).length}
						className={cn({ [styles.disabled]: !profession || !Object.keys(sections).length })}
					>
						Выгрузить в файл
					</Button>
				</div>
			</div>	
		</>
	);
}
